import { formatDateAtTime } from '../../utils/formatters';
import { CHIP_COLOURS } from './eventChipStyles';
import type { ApiEvent } from '@api/events';

/**
 * Single canonical derivation of an event's chips, shared by the events list
 * (EventManagement/pages/AllEvents/columns.tsx) and the order-detail event
 * card (OrderDetails/EventDetailFields.tsx). Before this module, each surface
 * re-derived its own chip set from a different source (real fields vs.
 * `event_requirements.toggles`), which is why they drifted (GRAIN-2814).
 *
 * Returns descriptors, not resolved strings: label resolution needs
 * `useTranslation()`'s `t()`, which this plain function doesn't have access
 * to — each surface resolves `labelKey`/`labelParams` at render time.
 *
 * A chip added here appears on both surfaces automatically ONLY if its
 * source field is present on both payloads. The events-list payload
 * (`to_list_h`) omits `metadata`, so the four `event_requirements.toggles`
 * chips below (no_grain_logo, no_pantry, delivery_buffer, stairs) can only
 * ever render on the order-detail card, which reads the full `ApiEvent`.
 */
export type EventChipGroup = 'date' | 'requirement';

export interface EventChip {
  key: string;
  labelKey: string;
  labelParams?: Record<string, string | number>;
  colours: string;
  group: EventChipGroup;
}

export type EventChipSource = Pick<
  ApiEvent,
  'kitchen_closed' | 'pre_setup_date_time' | 'security_clearance' | 'metadata'
>;

// Canonical order (GRAIN-2814): kitchen_closed sits in the list's row 2
// alongside date/times; everything else is a row-3 requirement chip.
export function deriveEventChips(source: EventChipSource): EventChip[] {
  const toggles = new Set(source.metadata?.event_requirements?.toggles ?? []);
  const stairs = source.metadata?.event_requirements?.stairs ?? false;

  const chips: EventChip[] = [];

  if (source.kitchen_closed) {
    chips.push({
      key: 'kitchen_closed',
      labelKey: 'chips.kitchen_closed',
      colours: CHIP_COLOURS.kitchenClosed,
      group: 'date',
    });
  }
  if (source.pre_setup_date_time) {
    chips.push({
      key: 'pre_setup',
      labelKey: 'chips.pre_setup',
      labelParams: { datetime: formatDateAtTime(source.pre_setup_date_time) },
      colours: CHIP_COLOURS.preSetup,
      group: 'requirement',
    });
  }
  if (source.security_clearance) {
    chips.push({
      key: 'security_clearance',
      labelKey: 'chips.security_clearance',
      colours: CHIP_COLOURS.securityClearance,
      group: 'requirement',
    });
  }
  if (toggles.has('no_grain_logo')) {
    chips.push({ key: 'no_grain_logo', labelKey: 'chips.no_grain_logo', colours: CHIP_COLOURS.grey, group: 'requirement' });
  }
  if (toggles.has('no_pantry')) {
    chips.push({ key: 'no_pantry', labelKey: 'chips.no_pantry', colours: CHIP_COLOURS.grey, group: 'requirement' });
  }
  if (toggles.has('delivery_buffer')) {
    chips.push({ key: 'delivery_buffer', labelKey: 'chips.delivery_buffer', colours: CHIP_COLOURS.grey, group: 'requirement' });
  }
  if (stairs) {
    chips.push({ key: 'stairs', labelKey: 'chips.stairs', colours: CHIP_COLOURS.grey, group: 'requirement' });
  }

  return chips;
}
